import { observer } from 'mobx-react'
import React, { useMemo } from 'react'

import { HomeOperationsReadinessStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  Badge,
  BadgeColors,
  BadgeColorsProps,
  formatDate,
  mainColors,
  Tooltip,
} from '@avantstay/backoffice-core'
import { FilterIndicator } from '@avantstay/data-grid'

import { storeHolder } from '@new/__global/components/FastTapeChart/stores/TapeChartStore'
import { ExtendedColumnKey } from '@new/__global/components/FiltersFramework'
import { convertToFilterRenderer } from '@new/__global/components/FiltersFramework/_utils'
import {
  ExtendedColumnCell,
  ExtendedHeaderCell,
} from '@new/domains/fastBookingsTapeChart/__commonStyles'
import {
  fastBookingsTapeChartFiltersStore,
  fastTapeChartFiltersConfig,
  HomeExtendedInfo,
  HomeFilterIds,
} from '@new/domains/fastBookingsTapeChart/internal'
import {
  hotelTapeChartFiltersConfig,
  UnitFilterIds,
} from '@new/domains/hotelTapeChart/config/filtersConfig'
import { HotelTapeChartFiltersStore } from '@new/domains/hotelTapeChart/stores/HotelTapeChartFiltersStore'

import { ROUTES } from '@routes'

import { arrivalModeColumnStore } from './ArrivalModeColumnStore'

const columnWidth = 96

const StatusHeader = observer(() => {
  const filterStore = storeHolder.store.filterStore as HotelTapeChartFiltersStore
  const isHotelContext = window.location.pathname.includes(ROUTES.hotelTapeChart.path)

  const currentFilterStore = isHotelContext ? filterStore : fastBookingsTapeChartFiltersStore

  const operationStatusConfig = useMemo(() => {
    return isHotelContext
      ? hotelTapeChartFiltersConfig.find(
          filter => filter.id === UnitFilterIds.OperationReadinessStatus,
        )
      : fastTapeChartFiltersConfig.find(
          filter => filter.id === HomeFilterIds.OperationReadinessStatus,
        )
  }, [isHotelContext])

  const operationStatusFilter = operationStatusConfig
    ? convertToFilterRenderer(operationStatusConfig, currentFilterStore, undefined)
    : null

  const isFilterActive = !!currentFilterStore.operationReadinessStatus
  return (
    <ExtendedHeaderCell
      key={`col-header-${ExtendedColumnKey.status}`}
      width={columnWidth}
      $iconColor={isFilterActive ? mainColors.accentMedium : undefined}
    >
      Status
      <FilterIndicator
        filterComponent={operationStatusFilter}
        alwaysVisible
        isFilterActive={isFilterActive}
      />
    </ExtendedHeaderCell>
  )
})

export const homeOperationalStatusBadgeInfo: Record<
  string,
  { badgeColor: BadgeColorsProps; label: string }
> = {
  [HomeOperationsReadinessStatus.blocked]: {
    badgeColor: BadgeColors.warning,
    label: 'Blocked',
  },
  [HomeOperationsReadinessStatus.notReady]: {
    badgeColor: BadgeColors.channelPersona,
    label: 'Not Ready',
  },
  [HomeOperationsReadinessStatus.occupied]: {
    badgeColor: BadgeColors.accent,
    label: 'Occupied',
  },
  [HomeOperationsReadinessStatus.ready]: {
    badgeColor: BadgeColors.success,
    label: 'Ready',
  },
}

const StatusCell = observer(({ home }: { home?: HomeExtendedInfo }) => {
  const { isLoadingExtendedInfo } = storeHolder.store?.apiStore

  if (isLoadingExtendedInfo) return <ExtendedColumnCell width={columnWidth}>...</ExtendedColumnCell>

  if (!home?.id) return <ExtendedColumnCell width={columnWidth}>-</ExtendedColumnCell>

  const readinessStatus =
    arrivalModeColumnStore.homeStatuses.get(home.id)?.status ??
    // fallback for fixed Status column
    home?.operationReadinessStatus?.status
  const lastUpdatedStatusAt = arrivalModeColumnStore.homeStatuses.get(home.id)?.statusUpdatedAt

  const currentStatus = readinessStatus
    ? homeOperationalStatusBadgeInfo[readinessStatus]
    : undefined

  return (
    <ExtendedColumnCell width={columnWidth}>
      {currentStatus ? (
        <Tooltip
          disabled={!lastUpdatedStatusAt}
          content={
            lastUpdatedStatusAt
              ? `Last updated at: ${formatDate(lastUpdatedStatusAt, 'MMM dd, yyyy - hh:mm a')}`
              : ''
          }
          placement="top"
        >
          <Badge badgeColor={currentStatus.badgeColor}>{currentStatus.label}</Badge>
        </Tooltip>
      ) : (
        '-'
      )}
    </ExtendedColumnCell>
  )
})

export const Status = {
  header: <StatusHeader />,
  columnKey: ExtendedColumnKey.status,
  width: columnWidth,
  renderer: (home?: HomeExtendedInfo) => (
    <StatusCell key={`col-${ExtendedColumnKey.status}-${home?.id}`} home={home} />
  ),
}
